import { subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { html, nothing } from 'lit';
import { map } from 'rxjs';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { mappingSalaryTableByKey } from '../api/salary-table-operations.js';
import { SalaryTable } from '../entities/SalaryTable.js';
import { allSalaryTableLevels } from '../enum/SalaryTableLevels.js';
import type { SalaryTableCol, SalaryTableRow } from '../enum/SalaryTableRow.js';
import { allSalaryTableRowKeys, type SalaryTableRowKeys } from '../enum/SalaryTableRowKeys.js';
import { showSalaryTableInsertDialog } from './salary-table-insert-popup.js';

/** The edited table: one row of level amounts per tariff group, keyed like the stored entities. */
type SalaryTableDocument = Record<string, SalaryTableCol[]>;

/** `E15UE` etc. is how the tariff groups are stored; `Ü` is how they are written. */
const readableRowKey = (key: SalaryTableRowKeys) => key.replaceAll('UE', 'Ü');

export const SalaryTableForm = stateful(() => {
  const levels = allSalaryTableLevels();
  const rowKeys = allSalaryTableRowKeys();

  const { value: rows, loading } = useQuery(
    () =>
      subscribeAllEntities(SalaryTable)(selectionSet(() => SalaryTable, 1)).pipe(
        map(allRows => mappingSalaryTableByKey((allRows as SalaryTable[]) ?? [])),
      ),
    [],
  );

  // bumped by "Abbrechen" to re-seed the form from the stored rows
  const [resetTick, setResetTick] = useState(0);

  // the form edits a plain copy of the level amounts; the entities are only touched on save
  const tableData = useMemo<SalaryTableDocument>(
    () => [Object.fromEntries(rowKeys.map(key => [key, [...(rows?.[key]?.levels ?? [])]]))],
    [rows, resetTick],
  );

  const form = useForm(
    tableData,
    {
      // the only decorated validators on SalaryTable are on `key` (not edited here) and a no-op on
      // `levels`, so there is nothing to validate on this screen
      validator: null,
    },
    [tableData],
  );

  const saveAllRows = async () => {
    await guardDialog(async () => {
      for (const key of rowKeys) {
        const row = rows?.[key];
        if (!row?._id) continue;

        // `levels` is a fixed six-slot tuple, so the edited row is projected back onto exactly
        // those six slots instead of whatever length the form array happens to have
        row.levels = levels.map((_level, index) => form.document[key]?.[index] ?? null) as SalaryTableRow;
        await row.save();
      }
    });
    SgSnackbar.show('Erfolgreich gespeichert!');
  };

  if (loading) return PageLoader({});

  return html`
    <div class="card-container">
      <div class="card card-body flex-col overflow-hidden">
        ${CardHeader({
          title: 'Tariftabellen',
          actionsDivider: true,
          actions: html`
            <button class="btn btn-ghost" @click=${() => showSalaryTableInsertDialog()}>Importieren</button>
            <button class="btn btn-ghost" @click=${() => setResetTick(resetTick + 1)}>Abbrechen</button>
            <button class="btn btn-primary" @click=${saveAllRows}>Speichern</button>
          `,
        })}

        <div class="overflow-auto">
          <table class="table table-zebra table-pin-rows table-pin-cols">
            <thead>
              <tr>
                <th></th>
                ${levels.map(level => html`<th class="text-center">${level}</th>`)}
              </tr>
            </thead>
            <tbody>
              ${rowKeys.map(key => {
                // rows the import has not produced yet have nothing to edit
                if (!rows?.[key]?.levels?.length) return nothing;

                return html`
                  <tr>
                    <th>${readableRowKey(key)}</th>
                    ${levels.map(
                      (_level, levelIndex) => html`
                        <td>${NumberInput({ ...form.field(key, levelIndex), step: 0.01 })}</td>
                      `,
                    )}
                  </tr>
                `;
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;
});
